/**
 * Message affiché à un CGP devant une information réservée aux admins du
 * cabinet — en page (sous-page des Réglages ouverte par un lien) ou en
 * ligne (à la place d'un lien « Compléter » qui mènerait à cette page).
 */

import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, UserRound } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useUsers } from "@/lib/api";
import { cn } from "@/lib/utils";

import { ADMIN_ONLY_ASK, ADMIN_ONLY_TITLE, adminNames, formatAdminNames } from "./admin-only";

function AdminContacts() {
  const { data: users } = useUsers();
  const list = adminNames(users ?? []);
  const names = formatAdminNames(list);
  if (!names) return null;
  return (
    <p className="flex items-center gap-1.5 text-sm text-ink-muted">
      <UserRound className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden />
      <span>
        {list.length > 1 ? "Admins" : "Admin"} du cabinet :{" "}
        <span className="font-medium text-ink">{names}</span>
      </span>
    </p>
  );
}

/** Page entière : icône, explication, contexte, admins, « Retour ». */
export function AdminOnlyPage({ reason }: { reason?: string | null }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) navigate(-1);
    else navigate("/reglages");
  };
  return (
    <section
      aria-labelledby="admin-only-title"
      className="mx-auto max-w-xl rounded-lg border border-cream-border bg-white px-6 py-10 text-center shadow-card"
    >
      <span
        aria-hidden
        className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-cream-hover text-ink"
      >
        <ShieldCheck className="h-6 w-6" />
      </span>
      <h2 id="admin-only-title" className="text-lg font-semibold tracking-tight text-ink">
        {ADMIN_ONLY_TITLE}
      </h2>
      {reason && <p className="mt-3 text-sm text-ink">{reason}</p>}
      <p className="mt-2 text-sm text-ink-muted">{ADMIN_ONLY_ASK}</p>
      <div className="mt-4 flex justify-center">
        <AdminContacts />
      </div>
      <Button variant="outline" className="mt-6" onClick={goBack}>
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Retour
      </Button>
    </section>
  );
}

/** En ligne, à la place d'un lien « Compléter » vers une page admin. */
export function AdminOnlyHint({
  reason,
  className,
}: {
  reason?: string | null;
  className?: string;
}) {
  return (
    <div
      role="note"
      className={cn(
        "flex items-start gap-3 rounded-md border border-cream-border bg-white px-3 py-2.5",
        className,
      )}
    >
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
      <div className="space-y-1 text-sm">
        {reason && <p className="text-ink">{reason}</p>}
        <p className="text-ink-muted">
          Ces informations sont réservées aux administrateurs du cabinet. {ADMIN_ONLY_ASK}
        </p>
        <AdminContacts />
      </div>
    </div>
  );
}
